import { useCallback } from 'react';
import { useLocation } from 'react-router';

import type { ApiUser } from 'api/userEnvironment/types/ApiUserEnvironment';
import { Box } from 'components/Box/Box';
import { StyledButtonOrLink } from 'components/Button/Button.styled';
import { Icon } from 'components/Icon/Icon';
import { Text } from 'components/Text/Text';
import { ClientOnly } from 'containers/ClientOnly';
import { useUserEnvironment } from 'contexts/UserEnvironmentContext';
import { useI18n } from 'i18n/hooks/useI18n';
import { ListingViewSidebarInfoBox } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebar.styled';
import type { ApiOrg } from 'modules/listing/orgs/api/types/ApiOrg';
import { fontSizes, legacySpace, spacing } from 'theme/theme';
import { showModal } from 'zustand-stores/modalStore';

function userAdminRequest(
  user: ApiUser | null | undefined,
  org: ApiOrg,
): boolean {
  return Boolean(user?.orgAdminRequests?.some((o) => o.orgId === org.id));
}

type Props = {
  org: ApiOrg;
  orgOwner: boolean;
};

export function ViewOrgPageSidebarDetails({ org, orgOwner }: Props) {
  const { i18n } = useI18n();

  const location = useLocation();
  const { user } = useUserEnvironment();
  const openAdminRequest = userAdminRequest(user, org);

  const handleAdminRequest = useCallback(() => {
    if (user) {
      showModal('ADMIN_REQUEST', {
        objectId: org.id,
        objectName: org.name,
      });
    } else {
      showModal('EMAIL_SIGNUP', {
        title:
          /* TRANSLATORS: title of dialog to request to be admin of org */
          i18n('Request to Become an Administrator'),
        redirectTo: `${location.pathname}?admin-request=true`,
      });
    }
  }, [i18n, user, org, location]);

  return (
    <>
      {!orgOwner && !openAdminRequest && (
        <ClientOnly>
          <ListingViewSidebarInfoBox>
            <Text mb={spacing[8]}>
              {i18n('Do you work for %(orgName)s?', { orgName: org.name })}
            </Text>

            <Box display="flex" alignItems="center">
              <StyledButtonOrLink
                type="button"
                className="link"
                style={{ fontSize: fontSizes[16] }}
                onClick={handleAdminRequest}
              >
                {i18n('Request administrator access')}
              </StyledButtonOrLink>

              <Box ml="4px">
                <Icon name="arrow-right" size={18} colorName="brandBlue" />
              </Box>
            </Box>
          </ListingViewSidebarInfoBox>
        </ClientOnly>
      )}
      {openAdminRequest && (
        <ClientOnly>
          <Text color="lightContentGrey" mb={legacySpace[3]}>
            {i18n(
              'You have a pending request to become an administrator for %(orgName)s',
              { orgName: org.name },
            )}
          </Text>
        </ClientOnly>
      )}
    </>
  );
}
